import { motion } from "framer-motion";
import { User, Building2, ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang, pick } from "../i18n";
import { useSite } from "../siteContext";

const scrollToSection = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

/* Card visuals — admin-replaceable via settings, fallback to bundled brand images */
const CARD_IMAGES = { individuals: "/card-individuals.jpg", organizations: "/card-organizations.jpg" };
const CardVisual = ({ variant, src }) => (
  <div className="relative h-28 mb-6 overflow-hidden rounded-2xl border border-luxury-border/60 bg-luxury-black" aria-hidden="true">
    <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(27,14,35,0.15), rgba(27,14,35,0.45))" }} />
  </div>
);

export const DUAL_PATHS = {
  ar: {
    eyebrow: "لمن نعمل",
    title_a: "رحلتان،",
    title_b: "رسالة واحدة",
    individuals: {
      label: "للأفراد",
      text: "لكل شخص يبحث عن تطوير ذاته، وتشافيه، ووعيه — رحلة تحوّل شخصية تبدأ من هنا.",
      points: ["دورات وبرامج رقمية", "تحوّل شخصي وتشافٍ", "جلسات فردية ١:١ مع مايا"],
      cta: "استكشفي الدورات",
      target: "courses",
    },
    organizations: {
      label: "للمؤسسات",
      text: "للشركات وقادة الموارد البشرية والفرق التي تبحث عن تجارب تطويرية راقية لموظفيها.",
      points: ["ورش عمل مؤسسية", "رفاه الموظفين وصحتهم النفسية", "القيادة والتطوير الشخصي", "محاضرات وتدريب وبرامج مخصصة"],
      cta: "Work With TheMAYA",
      target: "contact",
    },
  },
  en: {
    eyebrow: "WHO WE SERVE",
    title_a: "Two paths,",
    title_b: "one mission",
    individuals: {
      label: "For Individuals",
      text: "For anyone seeking growth, healing and self-awareness — a personal transformation journey starts here.",
      points: ["Online courses & programs", "Personal transformation & healing", "1:1 sessions with Maya"],
      cta: "Explore Courses",
      target: "courses",
    },
    organizations: {
      label: "For Organizations",
      text: "For companies, HR leaders and teams seeking premium development experiences for their people.",
      points: ["Corporate workshops", "Employee wellbeing", "Leadership & personal development", "Talks, training & tailored programs"],
      cta: "Work With TheMAYA",
      target: "contact",
    },
  },
};

export const DualPaths = () => {
  const { lang } = useLang();
  const settings = useSite();
  const s = DUAL_PATHS[lang];
  const g = (f) => pick(lang, settings, `dual_${f}`) || s[f];
  const imgs = {
    individuals: settings.dual_individuals_image || CARD_IMAGES.individuals,
    organizations: settings.dual_organizations_image || CARD_IMAGES.organizations,
  };
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;
  const cards = [
    { key: "individuals", data: s.individuals, icon: User, gold: false },
    { key: "organizations", data: s.organizations, icon: Building2, gold: true, to: "/corporate" },
  ];
  return (
    <section id="organizations" data-testid="dual-paths-section" className="relative py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{g("eyebrow")}</p>
          <h2 data-testid="dual-paths-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25]">
            {g("title_a")} <span className="gold-shimmer italic">{g("title_b")}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {cards.map((card, i) => (
            <motion.div
              key={card.key}
              data-testid={`path-card-${card.key}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className={`group rounded-3xl p-8 md:p-10 transition-all duration-500 hover:-translate-y-2 ${
                card.gold
                  ? "bg-luxury-gold/5 border border-luxury-gold/50 hover:border-luxury-gold hover:shadow-[0_20px_60px_rgba(201,164,92,0.18)]"
                  : "bg-luxury-surface border border-luxury-border hover:border-luxury-gold hover:shadow-[0_20px_60px_rgba(201,164,92,0.12)]"
              }`}
            >
              <CardVisual variant={card.key} src={imgs[card.key]} />
              <div className="w-14 h-14 rounded-2xl border border-luxury-border flex items-center justify-center mb-6 group-hover:border-luxury-gold group-hover:bg-luxury-gold/10 transition-all duration-500">
                <card.icon className="w-7 h-7 text-luxury-gold" strokeWidth={1.25} />
              </div>
              <h3 className="font-serif text-3xl text-luxury-bone mb-3">{card.data.label}</h3>
              <p className="text-luxury-gray text-sm leading-relaxed mb-6">{card.data.text}</p>
              <ul className="space-y-2.5 mb-8">
                {card.data.points.map((point) => (
                  <li key={point} className="flex items-center gap-3 text-sm text-luxury-bone/90">
                    <span className="w-1.5 h-1.5 rounded-full bg-luxury-gold shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
              {card.to ? (
                <Link
                  to={card.to}
                  data-testid={`path-cta-${card.key}`}
                  className="inline-flex items-center gap-3 font-bold px-8 py-4 rounded-full transition-[background-color,box-shadow,transform,color] duration-300 bg-luxury-gold text-luxury-black shadow-[0_12px_45px_-8px_rgba(209,160,92,0.55)] hover:bg-luxury-goldLight hover:shadow-[0_16px_60px_-6px_rgba(209,160,92,0.7)] hover:-translate-y-0.5"
                >
                  {card.data.cta}
                  <Arrow className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1 rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
              ) : (
                <button
                  data-testid={`path-cta-${card.key}`}
                  onClick={() => scrollToSection(card.data.target)}
                  className="inline-flex items-center gap-3 font-bold px-8 py-4 rounded-full transition-[background-color,box-shadow,transform,color] duration-300 border border-luxury-border text-luxury-bone hover:border-luxury-gold hover:text-luxury-gold"
                >
                  {card.data.cta}
                  <Arrow className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1 rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" strokeWidth={1.5} />
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
